/**
 * Input Component - RABBIT Design System
 */

import React, { forwardRef } from 'react';
import './Input.css';

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSize;
  error?: boolean;
  errorMessage?: string;
  label?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
  size = 'md',
  error = false,
  errorMessage,
  label,
  helperText,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className = '',
  id,
  ...props
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

  const wrapperClasses = [
    'ds-input-wrapper',
    fullWidth && 'ds-input-wrapper--full-width',
  ].filter(Boolean).join(' ');

  const inputContainerClasses = [
    'ds-input-container',
    `ds-input-container--${size}`,
    error && 'ds-input-container--error',
    leftIcon && 'ds-input-container--has-left-icon',
    rightIcon && 'ds-input-container--has-right-icon',
    props.disabled && 'ds-input-container--disabled',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={inputId} className="ds-input__label">
          {label}
        </label>
      )}
      <div className={inputContainerClasses}>
        {leftIcon && (
          <span className="ds-input__icon ds-input__icon--left">{leftIcon}</span>
        )}
        <input
          ref={ref}
          id={inputId}
          className="ds-input"
          {...props}
        />
        {rightIcon && (
          <span className="ds-input__icon ds-input__icon--right">{rightIcon}</span>
        )}
      </div>
      {(errorMessage || helperText) && (
        <span className={`ds-input__helper ${error ? 'ds-input__helper--error' : ''}`}>
          {error ? errorMessage : helperText}
        </span>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
